import React, { useMemo, useState } from 'react';
import { Sparkles, ArrowRight, RotateCcw } from 'lucide-react';
import { PRODUCTS } from '../data/products';

const MOODS = [
  { id: 'fresh', label: 'Fresh and clean', sub: 'Bright, airy, daily', category: 'Fresh & Bright', line: 'Showing fresh, clean scents' },
  { id: 'sweet', label: 'Sweet and warm', sub: 'Vanilla, caramel, amber', category: 'Sweet & Warm', line: 'Showing warm, sweet scents' },
  { id: 'dark', label: 'Dark and smoky', sub: 'Bold, leather, smoke', category: 'Dark & Bold', line: 'Showing dark, smoky scents' },
  { id: 'oud', label: 'Luxury oud', sub: 'Classic signature oud', category: 'Signature Oud', line: 'Showing signature oud scents' },
  { id: 'date', label: 'Date night', sub: 'Evening, statement', category: 'Dark & Bold', line: 'Showing evening statement scents', extra: /date|evening|night/i },
  { id: 'everyday', label: 'Everyday wear', sub: 'Easy, confident', category: 'Soft & Clean', line: 'Showing easy everyday scents', extra: /daily|everyday/i },
  { id: 'him', label: 'Gift for him', sub: 'Masculine, bold', category: 'Dark & Bold', line: 'Showing bold gift ideas', extra: /masculine|bold|strong|mature/i },
  { id: 'her', label: 'Gift for her', sub: 'Feminine, elegant', category: 'Floral & Elegant', line: 'Showing elegant gift ideas', extra: /feminine|elegant|floral/i },
];

const OCCASIONS = [
  { id: 'everyday', label: 'Everyday', test: (p) => /daily|everyday|daytime/i.test(p.bestFor) },
  { id: 'evening', label: 'Evening & date', test: (p) => /evening|night|date/i.test(p.bestFor) },
  { id: 'gift', label: 'A gift', test: (p) => p.giftReady && /gift/i.test(p.bestFor) },
  { id: 'statement', label: 'Statement', test: (p) => /statement|bold|strong|presence|impression/i.test(`${p.bestFor} ${p.profile}`) },
];

const recommend = (mood, occasion) => {
  if (!mood) return [];
  return PRODUCTS
    .map((p) => {
      let s = 0;
      if (p.category === mood.category) s += 3;
      if (mood.extra && mood.extra.test(`${p.bestFor} ${p.profile} ${p.category}`)) s += 1.5;
      if (occasion && occasion.test(p)) s += 2;
      if (p.featured) s += 0.5;
      return { p, s };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, 3)
    .map((x) => x.p);
};

export default function FragranceFinder({ onOpen, onApply }) {
  const [moodId, setMoodId] = useState(null);
  const [occId, setOccId] = useState(null);

  const mood = MOODS.find((m) => m.id === moodId) || null;
  const occasion = OCCASIONS.find((o) => o.id === occId) || null;
  const recs = useMemo(() => recommend(mood, occasion ? occasion.test : null), [mood, occasion]);

  const reset = () => { setMoodId(null); setOccId(null); };

  return (
    <section className="section quiz" id="finder">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow"><Sparkles size={12} /> Fragrance Concierge</span>
          <h2>Find your signature scent</h2>
          <p className="lede">Tell us the mood. We'll hand-pick the right oud for you.</p>
        </div>

        <div className="quiz-grid">
          {MOODS.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`quiz-opt ${moodId === m.id ? 'active' : ''}`}
              onClick={() => setMoodId(m.id)}
            >
              <span className="sub">{m.sub}</span>
              <span className="lbl">{m.label}</span>
            </button>
          ))}
        </div>

        {mood && (
          <div className="concierge">
            <div className="concierge-occ" role="radiogroup" aria-label="Occasion">
              <span className="concierge-occ-label">Worn for</span>
              {OCCASIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={occId === o.id}
                  className={`chip ${occId === o.id ? 'active' : ''}`}
                  onClick={() => setOccId(occId === o.id ? null : o.id)}
                >{o.label}</button>
              ))}
            </div>

            <p className="concierge-line">The concierge suggests</p>
            <div className="concierge-results">
              {recs.map((p, i) => (
                <button key={p.id} type="button" className="concierge-row" onClick={() => onOpen(p)}>
                  <span className="cr-rank">{['I', 'II', 'III'][i]}</span>
                  <img src={p.image} alt="" loading="lazy" />
                  <span className="cr-text">
                    <span className="cr-name">{p.name}</span>
                    <span className="cr-cat">{p.category}</span>
                    <span className="cr-desc">{p.profile}</span>
                  </span>
                  <span className="cr-go" aria-hidden="true"><ArrowRight size={15} /></span>
                </button>
              ))}
            </div>

            <div className="concierge-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onApply(mood)}>
                Browse all {mood.category} <ArrowRight size={13} />
              </button>
              <button type="button" className="concierge-reset" onClick={reset}>
                <RotateCcw size={12} /> Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
